/* eslint no-shadow: ["error", { "allow": ["state"] }] */
import Vue from 'vue';
import _ from 'lodash';
import {
  getAllFilesInServer,
  searchFilesByFileName,
  loadAttachmentById,
  updateAttachmentById,
  renameFileById,
} from '@/attachment/api';

const state = {
  activeModal: null,
  attachment: {},
  attachmentID: null,
  serverFiles: [],
  uploadedFiles: [],
  failedToUpload: [],
  errors: {},
  selectedAttachmentItemIndex: null,
  uploadSuccessfulCount: 0,
  page: 1,
  isInitialState: true,
  titleIsEmpty: false,
  hasFileOnServer: false,
  searchNotFound: false,
  statusOptions: [
    {
      key: 'published',
      name: 'เผยแพร่ทันที',
    },
    {
      key: 'publishOnAirAt',
      name: 'เผยแพร่เมื่อออกอากาศ',
    },
    {
      key: 'scheduled',
      name: 'กำหนดช่วงเวลาเผยแพร่',
    },
  ],
  loadAttachmentState: {
    isError: false,
    isLoading: false,
    error: null,
  },
  saveAttachmentState: {
    isSaving: false,
    isSaved: false,
    error: null,
  },
};

const defaultState = {
  activeModal: null,
  attachment: {},
  attachmentID: null,
  serverFiles: [],
  uploadedFiles: [],
  failedToUpload: [],
  errors: {},
  selectedAttachmentItemIndex: null,
  uploadSuccessfulCount: 0,
  page: 1,
  isInitialState: true,
  titleIsEmpty: false,
  hasFileOnServer: false,
  searchNotFound: false,
  statusOptions: [
    {
      key: 'published',
      name: 'เผยแพร่ทันที',
    },
    {
      key: 'publishOnAirAt',
      name: 'เผยแพร่เมื่อออกอากาศ',
    },
    {
      key: 'scheduled',
      name: 'กำหนดช่วงเวลาเผยแพร่',
    },
  ],
  loadAttachmentState: {
    isError: false,
    isLoading: false,
    error: null,
  },
  saveAttachmentState: {
    isSaving: false,
    isSaved: false,
    error: null,
  },
};

const getters = {
  attachment: (state) => state.attachment,
  attachmentID: (state) => state.attachmentID,
  activeModal: (state) => state.activeModal,
  errors: (state) => state.errors,
  hasFileOnServer: (state) => state.hasFileOnServer,
  serverFiles: (state) => state.serverFiles,
  searchNotFound: (state) => state.searchNotFound,
  statusOptions: (state) => state.statusOptions,
  page: (state) => state.page,
  titleIsEmpty: (state) => state.titleIsEmpty,
  uploadedFiles: (state) => state.uploadedFiles,
  uploadSuccessfulCount: (state) => state.uploadSuccessfulCount,
  failedToUpload: (state) => state.failedToUpload,
};

const mutations = {
  setActiveModal(state, newState) {
    state.activeModal = newState;
  },
  setAttachment(state, newState) {
    state.attachment = newState.payload.data;
  },
  setAttachmentID(state, newState) {
    state.attachmentID = newState;
  },
  async inheritAttachment(state, newState) {
    state.attachment = newState;
    await updateAttachmentById(state.attachmentID, newState);
  },
  setServerFiles(state, newState) {
    state.serverFiles = newState.payload.data;
    if (state.isInitialState) {
      state.hasFileOnServer = newState.payload.data.total;
      state.isInitialState = false;
    }
    state.searchNotFound = !newState.payload.data.total;
  },
  setPage(state, pageNumber) {
    state.page = pageNumber;
  },
  setTitleError(state) {
    state.titleIsEmpty = true;
  },
  removeTitleError(state) {
    state.titleIsEmpty = false;
  },
  setUploadedFiles(state, newState) {
    state.uploadedFiles = newState;
  },
  pushError(state, newState) {
    Vue.set(state.errors, newState.errorKey, newState.errorMessage);
  },
  pushUploadedFile(state, fileInfo) {
    state.uploadedFiles.push(fileInfo);
  },
  pushFailedToUpload(state, fileInfo) {
    state.failedToUpload.push(fileInfo);
  },
  increaseUploadSuccessfullyCount(state) {
    state.uploadSuccessfulCount += 1;
  },
  decreaseUploadSuccessfullyCount(state) {
    state.uploadSuccessfulCount -= 1;
  },
  async updateFileName(state, payload) {
    try {
      await renameFileById(state.attachmentID, payload);
    } catch (error) {
      // console.log(error);
    }
  },
  removeAllFailedUploads(state) {
    state.failedToUpload = [];
  },
  resetErrors(state) {
    state.errors = {};
  },
  resetFileBrowser(state) {
    state.uploadedFiles = [];
  },
  resetServerFiles(state) {
    state.searchNotFound = false;
    state.serverFiles.data = [];
    state.serverFiles.total = 0;
  },
  resetAttachment(state) {
    state.attachmentID = null;
    state.attachment = {};
  },
  resetAttachmentUploadSuccessfulCount(state) {
    state.uploadSuccessfulCount = 0;
  },
  resetAttachmentState(state) {
    Object.assign(state, _.cloneDeep(defaultState));
  },
};

const actions = {
  async loadAllFilesFromServer({ commit }, page) {
    commit('resetServerFiles');
    commit('setStateMessage', { type: 'loading' });
    try {
      commit('setServerFiles', { payload: await getAllFilesInServer(page) });
      commit('setStateMessage', { type: 'success' });
    } catch (error) {
      commit('setStateMessage', { type: 'error', payload: error.response });
    }
  },
  async loadAttachment({ commit }, attachmentID) {
    commit('resetAttachment');
    commit('setAttachmentID', attachmentID);
    commit('setStateMessage', { type: 'loading' });
    try {
      commit('setAttachment', { payload: await loadAttachmentById(attachmentID) });
      commit('setStateMessage', { type: 'success' });
    } catch (error) {
      commit('setStateMessage', { type: 'error', payload: error.response });
    }
  },
  async searchFile({ commit }, filename) {
    commit('resetServerFiles');
    commit('setStateMessage', { type: 'loading' });
    try {
      commit('setServerFiles', { payload: await searchFilesByFileName(filename) });
      commit('setStateMessage', { type: 'success' });
    } catch (error) {
      commit('setStateMessage', { type: 'error', payload: error.response });
    }
  },
  setPage({ commit }, pageNumber) {
    commit('setPage', pageNumber);
  },
  setAttachmentID({ commit }, attachmentID) {
    commit('setAttachmentID', attachmentID);
  },
  setServerFiles({ commit }, files) {
    commit('setServerFiles', files);
  },
  increaseUploadSuccessfullyCount({ commit }) {
    commit('increaseUploadSuccessfullyCount');
  },
  decreaseUploadSuccessfullyCount({ commit }) {
    commit('decreaseUploadSuccessfullyCount');
  },
  inheritAttachment({ commit }, newAttachment) {
    commit('inheritAttachment', newAttachment);
  },
  pushSelectedID({ commit }, id) {
    commit('pushSelectedID', id);
  },
  setUploadedFiles({ commit }, uploadedFile) {
    commit('setUploadedFiles', uploadedFile);
  },
  pushUploadedFile({ commit }, fileInfo) {
    commit('pushUploadedFile', fileInfo);
  },
  pushFailedToUpload({ commit }, fileInfo) {
    commit('pushFailedToUpload', fileInfo);
  },
  pushError({ commit }, errorKeyPair) {
    commit('pushError', errorKeyPair);
  },
  removeAllFailedUploads({ commit }) {
    commit('removeAllFailedUploads');
  },
  resetErrors({ commit }) {
    commit('resetErrors');
  },
  resetFileBrowser({ commit }) {
    commit('resetFileBrowser');
  },
  setActiveModal({ commit }, modalName) {
    commit('setActiveModal', modalName);
  },
  setTitleError({ commit }) {
    commit('setTitleError');
  },
  updateFileName({ commit }, file) {
    commit('updateFileName', file);
  },
  removeTitleError({ commit }) {
    commit('removeTitleError');
  },
  resetAttachment({ commit }) {
    commit('resetAttachment');
  },
  clearAttachmentUploadingStates({ commit }) {
    commit('resetFileBrowser');
    commit('resetServerFiles');
    commit('resetAttachmentUploadSuccessfulCount');
    commit('removeAllFailedUploads');
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
